import {Button, createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {formatCanvasVersionLabel} from 'canvas-core/canvasVersionUtil';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MantineDropdownMenu from 'ui-library/menus/MantineDropdownMenu';

export interface CanvasVersionOption {
  id: string;
  versionIndex: number;
}

const useStyles = createStyles((theme) => ({
  trigger: {
    borderRadius: theme.radius.xl,
  },
}));

export default function CanvasVersionDropdown({
  canvasDefinitionId,
  versions,
  selectedVersionId,
}: {
  canvasDefinitionId: string;
  versions: Array<CanvasVersionOption>;
  selectedVersionId: string | null;
}): JSX.Element | null {
  const {classes} = useStyles();

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.versionIndex - a.versionIndex),
    [versions]
  );

  if (sortedVersions.length === 0) {
    return null;
  }

  const selectedVersion =
    sortedVersions.find((version) => version.id === selectedVersionId) ?? sortedVersions[0];

  return (
    <MantineDropdownMenu
      position="bottom-start"
      sections={[
        {
          items: sortedVersions.map((version) => ({
            name: formatCanvasVersionLabel(version.versionIndex),
            onSelect: () => {
              FileBrowserActions.openFile(canvasDefinitionId, {versionApiId: version.id});
            },
            dataTestAttrs: {
              component: 'CanvasVersionDropdown',
              element: 'option',
              key: version.id,
            },
          })),
        },
      ]}
      trigger={
        <Button
          variant="secondary"
          size="xs"
          className={classes.trigger}
          aria-label="Select canvas version"
          rightIcon={<BenchlingIcon icon="chevron-down" />}
          {...getTestProps({component: 'CanvasVersionDropdown', element: 'trigger'})}
        >
          {formatCanvasVersionLabel(selectedVersion.versionIndex)}
        </Button>
      }
    />
  );
}
